import {
  TextField,
  Box,
  Typography,
  Divider,
  Stack,
  lighten,
  darken,
} from "@mui/material";
import { RegisterOptions, useFormContext } from "react-hook-form";
import { useAppSystem } from "@/global/hooks/useAppSystem";
import { InputErrorMessage } from "./InputErrorMessage";
import React from "react";
import { InputLabel } from "@/shared/common/formHelper/form_components";

interface Props {
  fieldName?: string;
  label?: string;
  options?: RegisterOptions;
  defaultValue?: string;
}

function insertChar(str: string, position: number, char: string) {
  return str.slice(0, position) + char + str.slice(position);
}

const formatPhoneNumber = (str: string) => {
  let formatted = str;
  if (str.length == 3) formatted = str + "-";
  if (str.length == 7) formatted = str + "-";
  if (str.length == 4 && str[4] !== "-") formatted = insertChar(str, 3, "-");
  if (str.length == 8 && str[8] !== "-") formatted = insertChar(str, 7, "-");
  return formatted;
};

export function PhoneNumberInput({
  fieldName = "phoneNumber",
  label,
  options,
  defaultValue,
}: Props) {
  const { theme } = useAppSystem();
  const {
    setValue,
    getValues,
    clearErrors,
    register,
    unregister,
    formState: { errors },
  } = useFormContext();

  const errorMessage = errors[fieldName]?.message as string;
  const [phoneNumber, setPhoneNumber] = React.useState(defaultValue ?? "");
  const [warningMessage, setWarningMessage] = React.useState("");

  const handleChange = (ev: any) => {
    let value = ev.target.value;
    const isNumber = /[0-9]/.test(ev.nativeEvent.data);
    const isBackspace = ev.nativeEvent.inputType === "deleteContentBackward";
    if (isBackspace) setPhoneNumber(value);
    if (isNumber && value.length <= 12) {
      setPhoneNumber(formatPhoneNumber(value));
    }
    clearErrors(fieldName);
    if (!isNumber || !isBackspace) return;
  };

  // registering to hook form ctx
  React.useEffect(() => {
    register(fieldName, {
      required: options?.required ?? "Phone Number is required",
      minLength: {
        value: 10,
        message: "",
      },
      maxLength: {
        value: 10,
        message: "",
      },
    });
  }, [register]);

  // setting value to hook form ctx and showing warning message
  React.useEffect(() => {
    const value = phoneNumber.replaceAll("-", "");
    setValue(fieldName, value);
    let timeout = setTimeout(() => {
      if ((value?.length < 10 && value?.length > 0) || value?.length > 10) {
        setWarningMessage("Phone Number should have 10 digits");
      } else {
        setWarningMessage("");
      }
      clearTimeout(timeout);
    }, 300);
  }, [phoneNumber]);

  // unregistering from hook form ctx
  React.useEffect(() => {
    return () => unregister(fieldName);
  }, []);

  return (
    <Box>
      {label ? (
        <Box py={"2px"} pl={"2px"}>
          <InputLabel>{label}</InputLabel>
        </Box>
      ) : null}

      <TextField
        size="small"
        fullWidth
        placeholder="Phone Number"
        onChange={handleChange}
        value={phoneNumber}
        InputProps={{
          startAdornment: (
            <Stack direction={"row"}>
              <Box>
                <Typography color={theme.palette.divider}>+1</Typography>
              </Box>
              <Box px={1}>
                <Divider orientation="vertical" />
              </Box>
            </Stack>
          ),
        }}
      />

      <Box
        p={warningMessage ? 1 : 0}
        mt={warningMessage ? 1 : 0}
        bgcolor={lighten(theme.palette.warning.main, 0.9)}
        borderRadius={"4px"}
        width={"100%"}
        height={warningMessage ? "24px" : "0px"}
        sx={{
          transition: "all 0.2s ease-in-out",
          opacity: warningMessage ? 1 : 0,
        }}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"left"}
      >
        <Typography
          variant="body2"
          color={darken(theme.palette.warning.main, 0.4)}
        >
          {warningMessage}
        </Typography>
      </Box>

      <Box>
        <InputErrorMessage message={errorMessage} />
      </Box>
    </Box>
  );
}
